/**
 * Authentication Utility Functions
 *
 * JWT parsing, validation, and token expiration checks
 */

import type { User } from '../types';
import CONFIG from '../config';

/**
 * Parse JWT token to extract user claims
 * @param token - JWT token string
 * @returns Parsed user object or null if parsing fails
 */
export function parseJwt(token: string): User | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    if (CONFIG.DEBUG) {
      console.error('Error parsing JWT:', error);
    }
    return null;
  }
}

/**
 * Check if a user's token is expired
 * @param user - User object with exp claim
 * @returns true if token is expired
 */
export function isTokenExpired(user: User | null): boolean {
  if (!user || !user.exp) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return user.exp < now;
}

/**
 * Validate token and user
 * @param token - JWT token string
 * @param user - User object parsed from token
 * @returns true if token and user are valid and not expired
 */
export function validateToken(
  token: string | null,
  user: User | null,
): boolean {
  if (!token || !user) {
    return false;
  }

  if (isTokenExpired(user)) {
    if (CONFIG.DEBUG) {
      console.log('Token expired');
    }
    return false;
  }

  return true;
}
